import { task } from 'ember-concurrency';
import moment from 'moment-timezone';
import { localeFromCode, Locales, LocalesArray } from 'volta/locales';
import { defaultErrorHandler } from 'volta/utils/error-utils';

// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed } from '@ember/object';
import Service, { service } from '@ember/service';

import type { TLocaleKey } from 'volta/locales';
import type { TError } from 'volta/utils/error-utils';

import type { IntlService } from 'ember-intl';
// @ts-expect-error
import type MomentService from 'ember-moment/services/moment';
import type StoreService from 'volta/services/store';
import type { Translations } from 'ember-intl/-private/utils/translations';
import type Translation from 'volta/models/translation';
import type PowerCalendar from 'ember-power-calendar/services/power-calendar';

export interface IWeekDayOption {
  key: number;
  iso: number;
  label: string;
  abbrv: string;
}

export default class LocalizationService extends Service {
  // Services
  // ~~~~~
  @service declare store: StoreService;
  @service declare intl: IntlService;
  @service declare moment: MomentService;
  @service declare powerCalendar: PowerCalendar;

  initialTranslations?: Translations;

  get guessedTimeZone() {
    return moment.tz?.guess();
  }

  get availableTimeZones() {
    return moment.tz?.names() ?? [];
  }

  @computed('moment.locale')
  get weekDays(): IWeekDayOption[] {
    return [...Array.from({ length: 7 }).keys()].map((day) => {
      const mLocalWeekday = moment().weekday(day);
      return {
        iso: mLocalWeekday.isoWeekday(),
        key: day + 1,
        label: mLocalWeekday.format('dddd'),
        abbrv: mLocalWeekday.format('ddd')
      };
    });
  }

  get defaultLocale() {
    const lang = this.browserLanguage;
    const existing = LocalesArray.find((l) => l.value.toLowerCase() === lang.toLowerCase());
    return existing ?? Locales['en-gb'];
  }

  setLocale(locale?: TLocaleKey) {
    this.intl.setLocale([locale ?? this.defaultLocale.value]);
    const momentLocale = (localeFromCode(locale) ?? this.defaultLocale).moment;
    this.moment.changeLocale(momentLocale);
    this.powerCalendar.locale = momentLocale;
  }

  setTimeZone(tz?: string) {
    this.moment.changeTimeZone(tz ?? this.guessedTimeZone);
    moment.tz.setDefault(tz ?? this.guessedTimeZone);
  }

  async setTranslation(locale = this.browserLanguage, forceUpdate = false) {
    if (!this.intl.locales.includes(locale) || forceUpdate) {
      const translation = await this.task.perform(locale);
      if (translation) {
        this.intl.addTranslations(translation.locale.toLowerCase(), translation.messages);
        this.setLocale(locale.toLowerCase() as TLocaleKey);
      }
    } else {
      this.setLocale(locale.toLowerCase() as TLocaleKey);
    }
  }

  get browserLanguage(): string {
    return navigator.language;
  }

  task = task(async (locale: string) => {
    try {
      return await this.store.findRecord<Translation>('translation', locale);
    } catch (error) {
      return defaultErrorHandler(error as TError);
    }
  });
}

declare module '@ember/service' {
  interface Registry {
    localization: LocalizationService;
  }
}
